import {
  createContext,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';

import type { ComponentWithChildren } from '@polyai/common/types/helpers';

interface ProjectLoadingContextValue {
  /** Nav/header loading - waiting for basic project/account data */
  isNavLoading: boolean;
  /** Content loading - waiting for full projection */
  isContentLoading: boolean;
  /** Page-specific loading - waiting for page data (e.g., dashboard) */
  isPageLoading: boolean;
  /** Combined loading state (any loading in progress) */
  isProjectLoading: boolean;
  setNavLoading: (loading: boolean) => void;
  setContentLoading: (loading: boolean) => void;
  setPageLoading: (loading: boolean) => void;
}

const ProjectLoadingContext = createContext<ProjectLoadingContextValue>({
  isNavLoading: true,
  isContentLoading: true,
  isPageLoading: false,
  isProjectLoading: true,
  setNavLoading: () => {},
  setContentLoading: () => {},
  setPageLoading: () => {},
});

export const ProjectLoadingProvider: ComponentWithChildren = ({ children }) => {
  const [isNavLoading, setIsNavLoading] = useState(true);
  const [isContentLoading, setIsContentLoading] = useState(true);
  const [isPageLoading, setIsPageLoading] = useState(false);

  const isProjectLoading = isNavLoading || isContentLoading || isPageLoading;

  const value = useMemo(
    () => ({
      isNavLoading,
      isContentLoading,
      isPageLoading,
      isProjectLoading,
      setNavLoading: setIsNavLoading,
      setContentLoading: setIsContentLoading,
      setPageLoading: setIsPageLoading,
    }),
    [isNavLoading, isContentLoading, isPageLoading, isProjectLoading],
  );

  return (
    <ProjectLoadingContext.Provider value={value}>
      {children}
    </ProjectLoadingContext.Provider>
  );
};

export const useProjectLoading = () => useContext(ProjectLoadingContext);

/**
 * Hook for pages to register their loading state with the overlay.
 * The overlay will stay visible until this loading completes.
 */
export const usePageLoading = (isLoading: boolean) => {
  const { setPageLoading } = useProjectLoading();

  useLayoutEffect(() => {
    setPageLoading(isLoading);
    return () => setPageLoading(false);
  }, [isLoading, setPageLoading]);
};
